import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import Navigation from './Navigation';
import AdvancedSearch from './AdvancedSearch';
import { API_ENDPOINTS } from '../config/api';
import {
  coordinatesFor,
  createTravelogueMarkerElement,
  createTraveloguePopupContent,
  hasValidCoordinates,
} from './mapHelpers';
import './MapPage.css';

// Set Mapbox token from environment variable
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const MapPage = () => {
  const [reliefs, setReliefs] = useState([]);
  const [tripReports, setTripReports] = useState([]);
  const [mapDataReady, setMapDataReady] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [travelogueLoadFailed, setTravelogueLoadFailed] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Load both map layers without letting one failed request block the other.
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      return;
    }

    let cancelled = false;

    Promise.allSettled([
      axios.get(API_ENDPOINTS.reliefs),
      axios.get(API_ENDPOINTS.tripReports),
    ]).then(([reliefResult, tripReportResult]) => {
      if (cancelled) {
        return;
      }

      if (reliefResult.status === 'fulfilled') {
        setReliefs(reliefResult.value.data);
        console.log('Reliefs loaded:', reliefResult.value.data);
      } else {
        console.error('Error fetching reliefs:', reliefResult.reason);
      }

      if (tripReportResult.status === 'fulfilled') {
        setTripReports(tripReportResult.value.data);
        console.log('Travelogue entries loaded:', tripReportResult.value.data);
      } else {
        setTravelogueLoadFailed(true);
        console.error('Error fetching travelogue entries:', tripReportResult.reason);
      }

      setMapDataReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize the Mapbox map once.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !mapContainerRef.current) {
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [0, 30],
      zoom: 1.5
    });
    mapRef.current = map;
    setMapReady(true);

    // Add navigation control (zoom in/out)
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Cleanup on unmount
    return () => {
      mapRef.current = null;
      map.remove();
    };
  }, []);

  // Update relief markers without rebuilding the map.
  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return;
    }

    const reliefMarkers = reliefs.flatMap(relief => {
      if (hasValidCoordinates(relief)) {
        const reliefMarker = new mapboxgl.Marker()
          .setLngLat(coordinatesFor(relief))
          .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(relief.title))
          .addTo(mapRef.current);

        return [reliefMarker];
      }

      return [];
    });

    return () => reliefMarkers.forEach(marker => marker.remove());
  }, [mapReady, reliefs]);

  // Add a visually distinct marker and linked popup for every mapped travelogue entry.
  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return;
    }

    const travelogueMarkers = tripReports
      .filter(hasValidCoordinates)
      .map(report => (
        new mapboxgl.Marker({
          element: createTravelogueMarkerElement(report),
          anchor: 'bottom',
        })
          .setLngLat(coordinatesFor(report))
          .setPopup(
            new mapboxgl.Popup({ offset: 34, maxWidth: '320px' })
              .setDOMContent(createTraveloguePopupContent(report))
          )
          .addTo(mapRef.current)
      ));

    return () => travelogueMarkers.forEach(marker => marker.remove());
  }, [mapReady, tripReports]);

  // Frame the complete mapped collection after both API requests settle.
  useEffect(() => {
    if (!mapReady || !mapDataReady || !mapRef.current) {
      return;
    }

    const mappedItems = [...reliefs, ...tripReports].filter(hasValidCoordinates);
    if (mappedItems.length === 0) {
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    mappedItems.forEach(item => bounds.extend(coordinatesFor(item)));
    mapRef.current.fitBounds(bounds, {
      padding: 72,
      maxZoom: 6,
      duration: 900,
    });
  }, [mapDataReady, mapReady, reliefs, tripReports]);

  const mappedReliefCount = reliefs.filter(hasValidCoordinates).length;
  const mappedTravelogueCount = tripReports.filter(hasValidCoordinates).length;
  const travelogueStatus = travelogueLoadFailed
    ? 'Travelogue locations could not load. Refresh the page to try again.'
    : !mapDataReady
      ? 'Loading travelogue locations…'
      : mappedTravelogueCount === 0
        ? 'No travelogue locations are mapped yet.'
        : `${mappedTravelogueCount} travelogue ${mappedTravelogueCount === 1 ? 'visit' : 'visits'} mapped`;

  return (
    <div className="page-container">
      <Navigation />
      {MAPBOX_TOKEN ? (
        <div className="map-page">
          <div className="search-container map-page__sidebar">
            <AdvancedSearch />
            <section className="map-legend" aria-labelledby="map-legend-title">
              <h2 id="map-legend-title">Map key</h2>
              <ul>
                <li>
                  <span className="map-legend__symbol map-legend__symbol--relief" aria-hidden="true"></span>
                  <span>Catalog reliefs</span>
                  <strong>{mappedReliefCount}</strong>
                </li>
                <li>
                  <span className="map-legend__symbol map-legend__symbol--travelogue" aria-hidden="true"></span>
                  <span>Travelogue visits</span>
                  <strong>{mappedTravelogueCount}</strong>
                </li>
              </ul>
              <p className="map-legend__status" aria-live="polite">{travelogueStatus}</p>
            </section>
          </div>
          <div className="map-container map-page__canvas" ref={mapContainerRef}></div>
        </div>
      ) : (
        <main className="map-error" role="alert">
          <div className="map-error-content">
            <h1>Interactive map unavailable</h1>
            <p>
              The map is temporarily unavailable because its access token is not configured.
              The rest of the Bas Relief Atlas remains available.
            </p>
          </div>
        </main>
      )}
    </div>
  );
};

export default MapPage;
